/**
 * Which Oura workspaces a maintenance notice may offer (#4901).
 *
 * A business owns one workspace (`aeo_businesses.company_id`), but a business
 * that was re-created leaves its old workspace behind, still a workspace
 * company and still named after the client. Listed as-is, staging showed
 * "The Lush Clinic" four times, and only one of them is the client staff see.
 * The picker therefore keeps an Oura workspace only when a business on
 * GET /api/v1/businesses (the Oura Businesses list) owns it.
 *
 * Read directly rather than through `listBusinesses`: that one answers [] on
 * failure, and here "no businesses" would hide every Oura client from a notice.
 */
import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const OURA_COMPANY_TYPE = "oura";

/** The `company_id` of every live business, or null when the list could not be read. */
export async function liveOuraCompanyIds(): Promise<Set<string> | null> {
    try {
        const token = getCookie("authToken");
        const res = await axios.get(`${apiURL}/api/v1/businesses`, {
            params: { limit: 500 },
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const body = res.data?.data ?? res.data;
        if (!Array.isArray(body?.businesses)) return null;
        const ids = new Set<string>();
        for (const b of body.businesses as { company_id?: string | null }[]) {
            if (b?.company_id) ids.add(b.company_id);
        }
        return ids;
    } catch (err) {
        catchErrorLog(`Maintenance: failed to load Oura businesses: ${err}`);
        return null;
    }
}

/**
 * Drop Oura workspaces no live business owns. Every other product passes
 * through untouched. `live` null (the list could not be read) keeps the full
 * list: a notice that reaches a stale login is better than one that silently
 * skips every Oura client.
 */
export function keepLiveOura<T extends { id: string; company_type?: string | null }>(
    companies: T[],
    live: Set<string> | null,
): T[] {
    if (!live) return companies;
    return companies.filter((c) => c.company_type !== OURA_COMPANY_TYPE || live.has(c.id));
}
